/*
 * 聊天接口。
 *
 * 三个端点，都挂在 `/api/v1/app` 下：
 *
 *   POST /api/v1/app/chat/enter     进入对话，拿今天消息的最新一页
 *   GET  /api/v1/app/chat/timeline  上拉，拿今天更早的一页
 *   POST /api/v1/app/chat/messages  说一句话，拿本轮新增的消息
 *
 * ## 为什么「进入对话」也是 POST
 *
 * 它会写库：可能新建会话、可能生成一句开场白、可能建出还不存在的关系。
 * 服务端每轮判定该不该开口，客户端不做任何判断也不传 conversationId——
 * 旧系统把 chatId 交给客户端，结果是库里出现跨 49 天、643 条消息的会话。
 *
 * ## 超时为什么放到 45 秒
 *
 * 默认的 8 秒是为读接口定的。这两个端点里面有一次真实的模型调用，
 * 实测 deep 档 4~8 秒、赶上重试会到 20 秒以上。用 8 秒的话客户端会在
 * 服务端还在生成时就判超时，然后用户点重试——那一轮其实已经在写库了。
 * 幂等键能挡住重复扣费，但界面上会先闪一次「没送出去」，很难看。
 */

import type { ChatEnterView, ChatTimelinePage, ChatTurnView } from "@dreamverse/contracts";
import { apiRequest } from "./api-client";

/** 一轮交互最多等多久。里面包着一次模型调用，不是普通读请求。 */
const CHAT_TIMEOUT_MS = 45_000;

/** 生成幂等键。同一条消息的所有重试共用它。 */
export const newClientMessageId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `cmsg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * 进入对话。
 *
 * 返回的 `messages` 里可能已经包含她刚说的开场白——要不要打招呼是服务端
 * 按会话边界判定的，客户端看到什么就画什么。
 */
export function enterChat(signal?: AbortSignal): Promise<ChatEnterView> {
  return apiRequest<ChatEnterView>("/api/v1/app/chat/enter", {
    method: "POST",
    timeoutMs: CHAT_TIMEOUT_MS,
    ...(signal ? { signal } : {}),
  });
}

/**
 * 取今天更早的消息。游标由服务端签发并原样回传；服务端会在今天 00:00 停住，
 * 不让聊天页顺着上拉混进昨天。
 */
export function fetchEarlierTodayMessages(
  before: string,
  signal?: AbortSignal,
): Promise<ChatTimelinePage> {
  const params = new URLSearchParams({ before });
  return apiRequest<ChatTimelinePage>(`/api/v1/app/chat/timeline?${params.toString()}`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * 说一句话。
 *
 * @param clientMessageId 幂等键。**重试必须复用同一个值**：换新键的话，
 *                        超时那次万一其实已经落库，就会产生两条重复消息
 *                        和两次扣费。
 */
export function sendChatMessage(
  input: { text: string; clientMessageId: string; imageAssetIds?: readonly string[] },
  signal?: AbortSignal,
): Promise<ChatTurnView> {
  return apiRequest<ChatTurnView>("/api/v1/app/chat/messages", {
    body: input,
    timeoutMs: CHAT_TIMEOUT_MS,
    ...(signal ? { signal } : {}),
  });
}

/** 上传一张待发送的聊天图片；真正绑定到消息发生在随后那次 send。 */
export function uploadChatImage(
  file: File,
  signal?: AbortSignal,
): Promise<{ assetId: string; url: string | null }> {
  const body = new FormData();
  body.append("file", file);
  return apiRequest<{ assetId: string; url: string | null }>("/api/v1/app/chat/images", {
    method: "POST",
    body,
    timeoutMs: 30_000,
    ...(signal ? { signal } : {}),
  });
}

/** 一条消息的评价。`null` 是「收回态度」，不是「没评过」。 */
export type MessageRating = 1 | -1 | null;

/**
 * 点赞 / 点踩 / 收回。
 *
 * 服务端按 `@@unique([messageId, userId])` upsert，一个人对一条消息只留一行。
 * **不影响羁绊与好感**——那两个数值只有一套来源（逐条消息的意图路由增量），
 * 从这里开第二个口子等于让人无成本刷数值。
 */
export function rateMessage(
  messageId: string,
  rating: MessageRating,
): Promise<{ rating: MessageRating }> {
  return apiRequest<{ rating: MessageRating }>(
    `/api/v1/app/messages/${encodeURIComponent(messageId)}/feedback`,
    { method: "POST", body: { rating } },
  );
}

/**
 * 删掉一条消息。
 *
 * 服务端做的是软删 **并同时**把它移出模型上下文——只让它从屏幕上消失、
 * 她却还记得，下一轮接着那个话题说，比没有删除更糟。
 *
 * 不退灵力：那一轮的模型调用已经真实发生。
 */
export function deleteMessage(messageId: string): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(
    `/api/v1/app/messages/${encodeURIComponent(messageId)}`,
    { method: "DELETE" },
  );
}
